<script setup>
import { X } from 'lucide-vue-next'
import EnquiryForm from '@/Components/Support/EnquiryForm.vue'
import { useScrollLock } from '@/composables/useScrollLock'

/**
 * Ask a question about a tutor before subscribing.
 *
 * There is no direct tutor messaging before a subscription — a conversation is
 * only created for an active Silver or Gold subscription — so a "Send message"
 * button on a public profile could not do what it said. Rather than invent a
 * feature or leave a dead button, this posts through the support channel that
 * already works: it records a contact, emails the team and acknowledges the
 * sender. The wording says exactly that.
 *
 * On a phone it rises from the bottom across the full width, as the help
 * panel does.
 */
const props = defineProps({
  open: { type: Boolean, default: false },
  tutorName: { type: String, required: true },
  tutorSlug: { type: String, required: true },
})

const emit = defineEmits(['close'])

useScrollLock(() => props.open)
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-200 motion-reduce:transition-none"
      leave-active-class="transition-opacity duration-150 motion-reduce:transition-none"
      enter-from-class="opacity-0" leave-to-class="opacity-0"
    >
      <div v-if="open" class="fixed inset-0 z-90 bg-ink/50" @click="emit('close')"></div>
    </Transition>

    <Transition
      enter-active-class="transition duration-200 ease-brand motion-reduce:transition-none"
      leave-active-class="transition duration-150 ease-in motion-reduce:transition-none"
      enter-from-class="opacity-0 translate-y-3" leave-to-class="opacity-0 translate-y-3"
    >
      <div
        v-if="open"
        class="fixed inset-x-0 bottom-0 z-100 max-h-[90dvh] overflow-y-auto rounded-t-2xl border border-line bg-surface shadow-lg sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:w-[min(30rem,calc(100dvw-2rem))] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-xl"
        role="dialog"
        aria-modal="true"
        :aria-label="`Message ${tutorName}`"
      >
        <div class="flex items-start justify-between gap-4 border-b border-line p-5">
          <div>
            <h2 class="text-lg">Message {{ tutorName }}</h2>
            <p class="mt-1 text-sm text-muted">
              Your message goes to the Bindle team, who will answer it or put you in touch.
            </p>
          </div>
          <button type="button" class="text-subtle hover:text-ink" aria-label="Close" @click="emit('close')">
            <X class="size-5" />
          </button>
        </div>

        <div class="p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
          <EnquiryForm
            id-prefix="enquiry"
            :tutor-slug="tutorSlug"
            :message-placeholder="`What would ${tutorName} do for a student struggling with…`"
            @close="emit('close')"
          />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
